import { useState, useEffect, Suspense } from "react";
import { useOutlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Header from "../ui/Header";
import Footer from "../ui/Footer";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { TerminalGrid } from "../ui/TerminalGrid";
import { pageVariants, pageTransition } from "@/utils/animations";
import { ErrorBoundary } from "../ui/ErrorBoundary";
import PageLoader from "../ui/PageLoader";

const BASE_WIDTH = 1440;
const BASE_HEIGHT = 900;

export default function Layout() {
  const [scale, setScale] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  // useOutlet() (not <Outlet/>) is required here: <Outlet/> is a stable proxy
  // that swaps its rendered children the instant the URL changes, even while
  // its ancestor motion.div is still mid-exit-animation for the OLD route.
  // That caused the new page's content to render inside the old, fading-out
  // container, then unmount/remount into a fresh one once the "exit"
  // finished — visually a fade-out-then-fade-in glitch. useOutlet() returns
  // the actual matched element, so the old element genuinely persists
  // through its own exit instead of being swapped out from under it.
  const outletElement = useOutlet();

  // The split typing screen (Battle/VsAi/Custom) is laid out to fit the
  // viewport on its own; letting `main` scroll on these routes just lets the
  // page jitter/scroll during a match instead of everything fitting in place.
  const noScrollRoutes = ["/battle", "/vs-ai", "/custom"];
  const disableMainScroll = noScrollRoutes.includes(location.pathname);

  useEffect(() => {
    const updateScale = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      
      // Mobile detection (< 640px width)
      const mobile = w < 640;
      setIsMobile(mobile);
      
      if (mobile) {
        // On mobile, don't scale - use natural responsive sizing
        setScale(1);
      } else {
        // On desktop, maintain aspect ratio with max scale of 1
        const s = Math.min(1, w / BASE_WIDTH, h / BASE_HEIGHT);
        setScale(s);
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <SettingsProvider>
      <div className="relative h-screen w-full bg-[#09090b] text-white flex flex-col overflow-hidden">

        {/* Background */}
        <TerminalGrid />

        {/* Fixed viewport frame — no page-level scroll. `overflow-hidden`
            (not `overflow-auto`) is deliberate: Header and Footer below are
            pinned and must never scroll out of view. Any page whose content
            doesn't fit gets its own internal scrollbar on `main` instead of
            the whole app scrolling. */}
        <div className="flex-1 overflow-hidden z-10 flex justify-center pointer-events-auto">
          <div
            className={`flex flex-col w-full pointer-events-auto ${isMobile ? "sm:max-w-none md:max-w-full lg:max-w-full" : ""}`}
            style={{
              // `zoom` (not `transform: scale`) so the scaled-down size is
              // reflected in real layout/scroll metrics — with `transform`,
              // the box still occupies its pre-scale layout height, so the
              // scrollable container and flex "stick to bottom" math end up
              // computed against the unscaled size, leaving the footer
              // either cut off or floating short of the true page bottom.
              zoom: isMobile ? 1 : scale,
              minWidth: isMobile ? "auto" : BASE_WIDTH,
              minHeight: isMobile ? "auto" : BASE_HEIGHT,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <Header />
            {/* min-h-0 overrides flex's default min-height:auto, which would
                otherwise let this grow to fit its content's natural size
                (pushing Footer below the fold) instead of being capped at
                its flex-1 allocation and scrolling internally. */}
            <main className={`flex-1 flex flex-col min-h-0 ${disableMainScroll ? "overflow-hidden" : "overflow-y-auto"}`}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="flex-1 flex flex-col min-h-0"
                >
                  {/* Scoped here (not in App.jsx) so a not-yet-fetched route
                      chunk only swaps this inner content for the loader —
                      Header/Footer/background stay mounted throughout. */}
                  <Suspense fallback={<PageLoader />}>
                    <ErrorBoundary>{outletElement}</ErrorBoundary>
                  </Suspense>
                </motion.div>
              </AnimatePresence>
            </main>
            <Footer />
          </div>
        </div>

      </div>
    </SettingsProvider>
  );
}